import { destinations } from "./destinations";
import type {
  ImageAsset,
  ItineraryDay,
  PackageAvailability,
  PackageCategory,
  TravelPackage,
} from "../types";

const mediaFor = (destinationSlug: string): { image: ImageAsset; gallery: ImageAsset[] } => {
  const destination = destinations.find((item) => item.slug === destinationSlug);

  if (!destination) {
    throw new Error(`Missing destination media for ${destinationSlug}`);
  }

  return {
    image: destination.image,
    gallery: destination.gallery,
  };
};

const standardRequirements = [
  "Valid government-issued ID for domestic travel",
  "Passport valid for at least six months for international travel",
  "Any visa, entry, or health documents required at the time of travel",
  "Final requirements will be rechecked before confirmation",
];

const standardFaqs = [
  {
    question: "Does this price confirm my booking?",
    answer:
      "No. The displayed amount is an indicative starting price. ACES will confirm availability and send a formal quotation after reviewing your inquiry.",
  },
  {
    question: "Can the itinerary be adjusted?",
    answer:
      "Yes. Tell us your preferred pace, room arrangement, and must-see stops in the inquiry builder, and our team will advise what can be customized.",
  },
  {
    question: "When will I receive payment instructions?",
    answer:
      "Payment details are provided manually by ACES only after you accept a formal quotation. The website does not collect travel payments.",
  },
];

interface PackageSeed {
  id: string;
  slug: string;
  title: string;
  tagline: string;
  destinationSlug: string;
  destination: string;
  country: string;
  category: PackageCategory;
  status: PackageAvailability;
  featured?: boolean;
  promo?: boolean;
  days: number;
  nights: number;
  travelDates: string[];
  departureCity?: string;
  price: number;
  overview: string;
  inclusions: string[];
  exclusions?: string[];
  itinerary: ItineraryDay[];
  accommodation: string;
  transportation: string;
  requirements?: string[];
  tags: string[];
  createdAt: string;
}

const createPackage = (seed: PackageSeed): TravelPackage => ({
  id: seed.id,
  slug: seed.slug,
  title: seed.title,
  tagline: seed.tagline,
  destinationSlug: seed.destinationSlug,
  destination: seed.destination,
  country: seed.country,
  category: seed.category,
  status: seed.status,
  featured: seed.featured ?? false,
  promo: seed.promo ?? false,
  ...mediaFor(seed.destinationSlug),
  duration: {
    days: seed.days,
    nights: seed.nights,
    label: `${seed.days} days / ${seed.nights} nights`,
  },
  travelDates: seed.travelDates,
  departureCity: seed.departureCity ?? "Manila",
  startingPrice: {
    amount: seed.price,
    currency: "PHP",
    qualifier: "per-person",
  },
  overview: seed.overview,
  inclusions: seed.inclusions,
  exclusions: seed.exclusions ?? [
    "Meals and activities not listed in the confirmed itinerary",
    "Travel insurance unless specifically added to the quotation",
    "Personal expenses, tips, and incidental charges",
  ],
  itinerary: seed.itinerary,
  accommodation: seed.accommodation,
  transportation: seed.transportation,
  requirements: seed.requirements ?? standardRequirements,
  paymentNotes:
    "No payment is collected on this website. A reservation and payment schedule will only be issued after availability is checked and you accept the ACES quotation.",
  changeNotes:
    "Changes and cancellations are subject to the final supplier rules shown in your quotation. Airfare, promotional rates, and time-sensitive reservations may be non-refundable.",
  faqs: standardFaqs,
  tags: seed.tags,
  createdAt: seed.createdAt,
});

export const travelPackages: TravelPackage[] = [
  createPackage({
    id: "pkg-boracay-sunset",
    slug: "boracay-sunset-escape",
    title: "Boracay Sunset Escape",
    tagline: "Four easy island days with room for sea, sun, and slow evenings.",
    destinationSlug: "boracay",
    destination: "Boracay",
    country: "Philippines",
    category: "domestic",
    status: "promo",
    featured: true,
    promo: true,
    days: 4,
    nights: 3,
    travelDates: ["September 10–13, 2026", "October 15–18, 2026", "November 12–15, 2026"],
    price: 12_990,
    overview:
      "A relaxed Boracay break pairing a convenient hotel base with island highlights and plenty of free time. Ideal for couples, small families, or a barkada that wants guidance without an overpacked schedule.",
    inclusions: [
      "Round-trip economy airfare from Manila",
      "Three nights in a selected Station 2 hotel",
      "Airport, land, and shared boat transfers",
      "Daily breakfast",
      "Shared island-hopping tour",
      "Local coordinator assistance",
    ],
    itinerary: [
      { day: 1, title: "Touch down and settle in", description: "Arrive in Caticlan, transfer to the island, check in, and enjoy a free sunset evening." },
      { day: 2, title: "Island-hopping day", description: "Join a shared island-hopping route with swimming stops and a local lunch.", meals: ["Breakfast", "Lunch"] },
      { day: 3, title: "Your Boracay day", description: "Keep the day free or ask us to add sailing, water sports, spa time, or a food crawl.", meals: ["Breakfast"] },
      { day: 4, title: "One last shoreline walk", description: "Breakfast, check-out, and coordinated transfers for your flight home.", meals: ["Breakfast"] },
    ],
    accommodation: "Three nights at a carefully selected mid-range hotel in or near Station 2. Final property and room category are confirmed in the quotation.",
    transportation: "Round-trip flight and shared transfers between Caticlan Airport and the hotel.",
    tags: ["beach", "island", "couples", "family", "barkada"],
    createdAt: "2026-07-18",
  }),
  createPackage({
    id: "pkg-el-nido-lagoons",
    slug: "el-nido-lagoon-days",
    title: "El Nido Lagoon Days",
    tagline: "A cinematic Palawan escape shaped around the water.",
    destinationSlug: "palawan",
    destination: "El Nido, Palawan",
    country: "Philippines",
    category: "domestic",
    status: "limited",
    featured: true,
    days: 4,
    nights: 3,
    travelDates: ["August 20–23, 2026", "November 5–8, 2026", "February 18–21, 2027"],
    price: 18_900,
    overview:
      "See El Nido’s limestone formations and clear lagoons with transfers, a comfortable town-base stay, and one full island tour arranged in advance.",
    inclusions: [
      "Round-trip airfare from Manila",
      "Three nights in a town-center hotel",
      "Round-trip airport transfers",
      "Daily breakfast",
      "Shared island tour with lunch and standard fees",
      "On-trip assistance",
    ],
    itinerary: [
      { day: 1, title: "Welcome to El Nido", description: "Fly to Palawan, meet your transfer, and settle into your hotel." },
      { day: 2, title: "Lagoons and limestone", description: "Spend the day on a shared island route with swim and kayak opportunities.", meals: ["Breakfast", "Lunch"] },
      { day: 3, title: "Choose your own horizon", description: "Free day for another island route, Nacpan Beach, or a gentle town day.", meals: ["Breakfast"] },
      { day: 4, title: "Homeward", description: "Breakfast and transfer for your return flight.", meals: ["Breakfast"] },
    ],
    accommodation: "Three nights in a selected El Nido town hotel. Beachfront upgrades may be requested.",
    transportation: "Round-trip flight plus shared van or hotel transfers; island tour by licensed local operator.",
    tags: ["lagoon", "nature", "island-hopping", "palawan"],
    createdAt: "2026-07-11",
  }),
  createPackage({
    id: "pkg-siargao-rhythm",
    slug: "siargao-island-rhythm",
    title: "Siargao Island Rhythm",
    tagline: "Surf-town energy balanced with lagoon-blue days.",
    destinationSlug: "siargao",
    destination: "Siargao",
    country: "Philippines",
    category: "domestic",
    status: "available",
    featured: true,
    days: 5,
    nights: 4,
    travelDates: ["September 6–10, 2026", "October 4–8, 2026", "March 7–11, 2027"],
    price: 21_500,
    overview:
      "A five-day island plan with a General Luna base, a three-island experience, and open time for surf lessons, cafés, or a Sugba Lagoon add-on.",
    inclusions: [
      "Round-trip airfare from Manila",
      "Four nights in a General Luna hotel",
      "Airport transfers",
      "Daily breakfast",
      "Three-island shared tour with lunch",
      "Local assistance",
    ],
    itinerary: [
      { day: 1, title: "Into island time", description: "Arrive, transfer to General Luna, and enjoy a free afternoon." },
      { day: 2, title: "Three-island route", description: "Visit a changing route of nearby sandbars and islands, subject to sea conditions.", meals: ["Breakfast", "Lunch"] },
      { day: 3, title: "Inland Siargao", description: "Optional land tour to palms, pools, and coastal viewpoints.", meals: ["Breakfast"] },
      { day: 4, title: "Your pace", description: "Try a surf lesson, visit cafés, or slow down by the water.", meals: ["Breakfast"] },
      { day: 5, title: "Fly home", description: "Breakfast, check-out, and airport transfer.", meals: ["Breakfast"] },
    ],
    accommodation: "Four nights in a selected General Luna hotel, standard room, based on double or twin sharing.",
    transportation: "Round-trip flight, shared airport transfers, and boat transportation during the included island tour.",
    tags: ["surf", "friends", "nature", "island"],
    createdAt: "2026-07-02",
  }),
  createPackage({
    id: "pkg-batanes-unhurried",
    slug: "batanes-unhurried",
    title: "Batanes, Unhurried",
    tagline: "Green horizons, Ivatan stories, and room to breathe.",
    destinationSlug: "batanes",
    destination: "Batanes",
    country: "Philippines",
    category: "domestic",
    status: "upcoming",
    days: 4,
    nights: 3,
    travelDates: ["March 11–14, 2027", "April 15–18, 2027"],
    price: 28_500,
    overview:
      "A small-group introduction to Batanes with North and South Batan touring, local meals, and sensible flexibility for the islands’ fast-changing weather.",
    inclusions: [
      "Round-trip airfare from Manila",
      "Three nights in an accredited local lodge or hotel",
      "Airport transfers",
      "Daily breakfast",
      "North and South Batan guided tours",
      "Selected tour meals and standard entrance fees",
    ],
    itinerary: [
      { day: 1, title: "Arrival and North Batan", description: "Meet your local guide and begin with northern viewpoints as weather allows.", meals: ["Lunch"] },
      { day: 2, title: "South Batan stories", description: "Explore heritage villages, coastal scenery, and Ivatan culture.", meals: ["Breakfast", "Lunch"] },
      { day: 3, title: "Flexible island day", description: "Reserve the day for Sabtang, a weather-adjusted tour, or a quiet Basco experience.", meals: ["Breakfast"] },
      { day: 4, title: "Depart Batanes", description: "Breakfast and airport transfer for your flight to Manila.", meals: ["Breakfast"] },
    ],
    accommodation: "Three nights at a selected Basco lodging. Simple, comfortable local accommodations are part of the Batanes experience.",
    transportation: "Round-trip flight, airport transfers, and air-conditioned local touring vehicle.",
    tags: ["culture", "landscape", "small-group", "slow-travel"],
    createdAt: "2026-06-21",
  }),
  createPackage({
    id: "pkg-tokyo-kyoto",
    slug: "tokyo-kyoto-first-look",
    title: "Tokyo & Kyoto First Look",
    tagline: "Neon evenings, quiet shrines, and Japan’s beautiful contrasts.",
    destinationSlug: "japan",
    destination: "Tokyo & Kyoto",
    country: "Japan",
    category: "international",
    status: "limited",
    featured: true,
    days: 7,
    nights: 6,
    travelDates: ["November 16–22, 2026", "March 22–28, 2027"],
    price: 69_900,
    overview:
      "A balanced first visit connecting Tokyo and Kyoto with guided highlights, independent discovery time, and practical assistance for documents and trip preparation.",
    inclusions: [
      "Round-trip economy airfare from Manila",
      "Six hotel nights based on twin sharing",
      "Daily breakfast",
      "Airport transfers",
      "Intercity rail ticket",
      "Selected Tokyo and Kyoto guided touring",
      "Visa document checklist assistance",
    ],
    exclusions: [
      "Japan visa fee or service charge unless added to the final quotation",
      "Meals and attractions not listed",
      "Travel insurance and personal expenses",
    ],
    itinerary: [
      { day: 1, title: "Manila to Tokyo", description: "Fly to Tokyo and transfer to your hotel." },
      { day: 2, title: "Tokyo essentials", description: "A guided day blending a historic district, a modern neighborhood, and skyline views.", meals: ["Breakfast"] },
      { day: 3, title: "Tokyo your way", description: "Free day for shopping, museums, theme parks, or an optional day trip.", meals: ["Breakfast"] },
      { day: 4, title: "Ride to Kyoto", description: "Travel by rail and settle into Kyoto for an open evening.", meals: ["Breakfast"] },
      { day: 5, title: "Kyoto heritage", description: "Visit selected temples, lanes, and a traditional district with a local guide.", meals: ["Breakfast"] },
      { day: 6, title: "Kansai free day", description: "Choose more Kyoto, a Nara excursion, or an Osaka food evening.", meals: ["Breakfast"] },
      { day: 7, title: "Return to Manila", description: "Check out and transfer for your flight home.", meals: ["Breakfast"] },
    ],
    accommodation: "Six nights across selected city hotels. Exact properties are provided after a live availability check.",
    transportation: "Round-trip international flight, airport transfers, intercity rail, and transport used on included tours.",
    tags: ["japan", "first-time", "culture", "city", "rail"],
    createdAt: "2026-07-20",
  }),
  createPackage({
    id: "pkg-seoul-autumn",
    slug: "seoul-autumn-stories",
    title: "Seoul Autumn Stories",
    tagline: "Golden leaves, palace paths, and lively neighborhood nights.",
    destinationSlug: "south-korea",
    destination: "Seoul",
    country: "South Korea",
    category: "international",
    status: "available",
    featured: true,
    days: 5,
    nights: 4,
    travelDates: ["October 22–26, 2026", "November 5–9, 2026"],
    price: 44_900,
    overview:
      "A city-focused Seoul escape with classic landmarks, a scenic day beyond the capital, and time to follow your own food, beauty, or K-culture interests.",
    inclusions: [
      "Round-trip economy airfare",
      "Four hotel nights based on twin sharing",
      "Daily breakfast",
      "Airport transfers",
      "Seoul highlights tour",
      "Nami Island area day trip",
      "Visa document checklist assistance",
    ],
    itinerary: [
      { day: 1, title: "Arrive in Seoul", description: "Airport welcome, hotel transfer, and free evening." },
      { day: 2, title: "Palaces and neighborhoods", description: "See a selected palace, heritage lanes, and a modern Seoul district.", meals: ["Breakfast"] },
      { day: 3, title: "Beyond the city", description: "Enjoy a seasonal day trip around Nami Island or a comparable scenic route.", meals: ["Breakfast"] },
      { day: 4, title: "Make Seoul yours", description: "A free day for shopping, cafés, museums, or optional experiences.", meals: ["Breakfast"] },
      { day: 5, title: "Return home", description: "Breakfast and airport transfer.", meals: ["Breakfast"] },
    ],
    accommodation: "Four nights in a selected Seoul hotel near convenient public transportation.",
    transportation: "Round-trip flight, airport transfers, and touring transportation on included days.",
    tags: ["south-korea", "autumn", "shopping", "culture"],
    createdAt: "2026-07-14",
  }),
  createPackage({
    id: "pkg-vietnam-heritage",
    slug: "hanoi-ha-long-heritage",
    title: "Hanoi & Ha Long Heritage",
    tagline: "Old Quarter flavors and limestone horizons in one easy journey.",
    destinationSlug: "vietnam",
    destination: "Hanoi & Ha Long Bay",
    country: "Vietnam",
    category: "international",
    status: "promo",
    promo: true,
    days: 5,
    nights: 4,
    travelDates: ["September 17–21, 2026", "February 11–15, 2027"],
    price: 34_500,
    overview:
      "Pair Hanoi’s layered street life with an overnight Ha Long Bay experience, leaving just enough independent time for coffee, food, and thoughtful wandering.",
    inclusions: [
      "Round-trip economy airfare",
      "Three city hotel nights and one cruise night",
      "Daily breakfast plus cruise meals",
      "Airport and intercity transfers",
      "Hanoi guided orientation",
      "Shared Ha Long Bay cruise program",
    ],
    itinerary: [
      { day: 1, title: "Hello, Hanoi", description: "Arrive, transfer, and ease into the Old Quarter." },
      { day: 2, title: "Hanoi layers", description: "Explore selected historical sites and food-rich streets.", meals: ["Breakfast"] },
      { day: 3, title: "Cruise into Ha Long", description: "Transfer to the bay and board an overnight shared cruise.", meals: ["Breakfast", "Lunch", "Dinner"] },
      { day: 4, title: "Bay morning, city evening", description: "Morning cruise activities, brunch, then return to Hanoi.", meals: ["Brunch"] },
      { day: 5, title: "Homeward", description: "Free time as flight schedules allow, then airport transfer.", meals: ["Breakfast"] },
    ],
    accommodation: "Three nights in Hanoi plus one night aboard a selected Ha Long Bay cruise vessel.",
    transportation: "Round-trip flight, airport transfers, Hanoi–Ha Long shared coach, and cruise vessel.",
    tags: ["vietnam", "food", "heritage", "cruise"],
    createdAt: "2026-07-16",
  }),
  createPackage({
    id: "pkg-singapore-family",
    slug: "singapore-family-city-break",
    title: "Singapore Family City Break",
    tagline: "A smooth first international adventure for every generation.",
    destinationSlug: "singapore",
    destination: "Singapore",
    country: "Singapore",
    category: "international",
    status: "available",
    featured: true,
    days: 4,
    nights: 3,
    travelDates: ["October 29–November 1, 2026", "December 3–6, 2026", "March 25–28, 2027"],
    price: 36_900,
    overview:
      "A family-friendly city break with airport support, a convenient hotel, and a guided introduction—plus an open day for Sentosa, wildlife parks, or a pace that suits your group.",
    inclusions: [
      "Round-trip economy airfare",
      "Three hotel nights based on selected room arrangement",
      "Airport transfers",
      "Daily breakfast",
      "Half-day city orientation",
      "Trip preparation assistance",
    ],
    itinerary: [
      { day: 1, title: "Arrive with ease", description: "Meet your transfer and settle into your Singapore hotel." },
      { day: 2, title: "City highlights", description: "A guided introduction to key districts and waterfront landmarks.", meals: ["Breakfast"] },
      { day: 3, title: "Family choice day", description: "Choose Sentosa, gardens, wildlife attractions, museums, or an easy food-led day.", meals: ["Breakfast"] },
      { day: 4, title: "Until next time", description: "Breakfast and airport transfer.", meals: ["Breakfast"] },
    ],
    accommodation: "Three nights in a selected centrally connected hotel. Family-room configurations are subject to availability.",
    transportation: "Round-trip flight, airport transfers, and touring vehicle during the city orientation.",
    tags: ["singapore", "family", "first-international", "city"],
    createdAt: "2026-07-09",
  }),
  createPackage({
    id: "pkg-thailand-markets",
    slug: "bangkok-markets-and-temples",
    title: "Bangkok Markets & Temples",
    tagline: "A colorful long weekend of flavor, heritage, and city energy.",
    destinationSlug: "thailand",
    destination: "Bangkok",
    country: "Thailand",
    category: "international",
    status: "sold-out",
    days: 4,
    nights: 3,
    travelDates: ["August 27–30, 2026"],
    price: 29_900,
    overview:
      "A compact Bangkok introduction blending important cultural sights, market energy, and flexible time for shopping or a food-focused night out.",
    inclusions: [
      "Round-trip economy airfare",
      "Three hotel nights",
      "Daily breakfast",
      "Airport transfers",
      "Temple and city highlights tour",
      "Local coordinator assistance",
    ],
    itinerary: [
      { day: 1, title: "Bangkok arrival", description: "Transfer to the hotel and enjoy a free evening." },
      { day: 2, title: "Temple city", description: "Visit selected heritage landmarks with a local guide.", meals: ["Breakfast"] },
      { day: 3, title: "Market and city day", description: "Choose shopping, an optional day trip, or a self-guided food trail.", meals: ["Breakfast"] },
      { day: 4, title: "Return to Manila", description: "Breakfast and airport transfer.", meals: ["Breakfast"] },
    ],
    accommodation: "Three nights in a selected Bangkok hotel near public transportation.",
    transportation: "Round-trip flight, airport transfers, and included tour transport.",
    tags: ["thailand", "food", "shopping", "temples"],
    createdAt: "2026-06-29",
  }),
  createPackage({
    id: "pkg-cruise-strait",
    slug: "singapore-strait-cruise",
    title: "Singapore Strait Cruise Escape",
    tagline: "A city stay and short sailing, planned as one seamless inquiry.",
    destinationSlug: "singapore",
    destination: "Singapore & the Malacca Strait",
    country: "Singapore",
    category: "cruise",
    status: "upcoming",
    days: 6,
    nights: 5,
    travelDates: ["January 14–19, 2027", "March 4–9, 2027"],
    price: 58_900,
    overview:
      "Begin with time in Singapore before boarding a short regional sailing. Sailing route, vessel, cabin, and port calls will be confirmed against the live cruise schedule.",
    inclusions: [
      "Round-trip economy airfare",
      "Two Singapore hotel nights",
      "Three cruise nights in an interior cabin",
      "Meals aboard the cruise as provided by the operator",
      "Airport, hotel, and cruise-port transfers",
      "Pre-departure document briefing",
    ],
    exclusions: [
      "Cruise gratuities and onboard purchases",
      "Optional shore excursions",
      "Travel insurance and personal expenses",
      "Any visa required for the confirmed ports of call",
    ],
    itinerary: [
      { day: 1, title: "Arrive in Singapore", description: "Hotel transfer and free evening." },
      { day: 2, title: "City at your pace", description: "Explore independently or add a city experience.", meals: ["Breakfast"] },
      { day: 3, title: "Board your cruise", description: "Transfer to the cruise terminal and embark.", meals: ["Breakfast", "Dinner"] },
      { day: 4, title: "Port or sea day", description: "Follow the confirmed sailing program.", meals: ["Breakfast", "Lunch", "Dinner"] },
      { day: 5, title: "A second day afloat", description: "Enjoy the vessel and any scheduled destination stop.", meals: ["Breakfast", "Lunch", "Dinner"] },
      { day: 6, title: "Disembark and fly home", description: "Port pick-up and airport transfer.", meals: ["Breakfast"] },
    ],
    accommodation: "Two hotel nights and three nights in an interior cruise cabin. Upgrades can be requested.",
    transportation: "Round-trip flight, private or shared ground transfers, and cruise vessel.",
    tags: ["cruise", "singapore", "couples", "family"],
    createdAt: "2026-07-05",
  }),
  createPackage({
    id: "pkg-custom-sea",
    slug: "southeast-asia-your-way",
    title: "Southeast Asia, Your Way",
    tagline: "A custom multi-city plan built around your time, pace, and budget.",
    destinationSlug: "singapore",
    destination: "Southeast Asia",
    country: "Multiple destinations",
    category: "custom",
    status: "available",
    days: 8,
    nights: 7,
    travelDates: ["Flexible dates on request"],
    price: 52_000,
    overview:
      "Start with Singapore, Bangkok, Hanoi, or another regional gateway, then let our team shape a sensible multi-city route. The sample price is only a planning guide for a two-city journey.",
    inclusions: [
      "Custom itinerary consultation",
      "Proposed flight and hotel options",
      "Airport-transfer options",
      "Tour and experience recommendations",
      "Travel-requirement checklist",
      "One consolidated quotation",
    ],
    exclusions: [
      "Any item not accepted in the final custom quotation",
      "Travel insurance unless requested",
      "Personal expenses and supplier charges arising from changes",
    ],
    itinerary: [
      { day: 1, title: "Your first gateway", description: "Arrive in the first city selected during consultation." },
      { day: 2, title: "Designed around you", description: "Guided highlights or independent discovery, based on your priorities." },
      { day: 3, title: "Go deeper", description: "A second day for food, culture, family activities, or leisure." },
      { day: 4, title: "Connect onward", description: "Travel to your second destination." },
      { day: 5, title: "A new city, your pace", description: "Begin exploring with optional guided support." },
      { day: 6, title: "Signature experience", description: "Add the activity that matters most to your group." },
      { day: 7, title: "Unscripted time", description: "Leave space for rest, shopping, or a spontaneous favorite." },
      { day: 8, title: "Journey home", description: "Coordinated departure based on the accepted itinerary." },
    ],
    accommodation: "A mix of selected hotels aligned to your comfort level and preferred neighborhoods.",
    transportation: "Flight, rail, private transfer, and local-tour options are proposed based on the final route.",
    tags: ["custom", "multi-city", "southeast-asia", "flexible"],
    createdAt: "2026-07-22",
  }),
];

export const packages = travelPackages;

